import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Button, { ButtonType } from 'components/Button/Button';
import FormContainer from 'components/LoanForm/FormContainer';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getLoanOffer } from 'selectors/getLoanOfferData';
import { getApplicationData as selectApplicationData } from 'selectors/getApplicationData';
import { FlowComponentType } from 'routes/types';
import useLoanOfferProcessor from 'hooks/useLoanOfferProcessor';
import { updateLoanOffer } from 'thunks';
import { ApplyingResult } from 'enums/FlowNextResults';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';

import styles from './UnfreezeCreditScore.module.scss';

const EQUIFAX_URL = 'https://www.equifax.com/personal/credit-report-services/credit-freeze';

const providers = [
  {
    name: 'Equifax',
    url: EQUIFAX_URL,
    phone: '(888) 298-0045',
  },
];

enum UnfreezeMethod {
  Online = 'online',
  Phone = 'phone',
}

const UnfreezeCreditScore = ({ handleNext }: FlowComponentType): JSX.Element => {
  const [rerunFailed, setRerunFailed] = useState(false);

  const { isLoading } = useSelector(getLoanOffer);
  const { application } = useSelector(selectApplicationData);

  const { processLoanOffer } = useLoanOfferProcessor({
    handleNext,
  });

  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const handleRerunOffer = async () => {
    setRerunFailed(false);

    const runLoanOffer = () => dispatchWithUnwrap(updateLoanOffer({ applicationId: application!.id }));

    const res = await processLoanOffer(runLoanOffer);

    if (!res || res.data.credit_passed) {
      handleNext(ApplyingResult.Done);
    } else {
      setRerunFailed(true);
    }
  };

  const handleAnalytics = (provider: string, method: UnfreezeMethod) => {
    analytics.track('Unfreeze Credit Score', {
      provider,
      method,
      applicationId: application!.id,
    });
  };

  return (
    <FormContainer
      title="Unfreeze your credit"
      subtitle="It looks like your Equifax credit file is frozen. Contact Equifax to temporarily lift the freeze and continue your application."
      className={styles.container}
    >
      {providers.map((provider) => (
        <div key={provider.name} className={styles.providerContainer}>
          <h4 className={styles.providerText}>{provider.name}</h4>
          <Button
            type={ButtonType.Transparent}
            onClick={() => {
              window.open(provider.url, '_blank', 'noopener,noreferrer');
              handleAnalytics(provider.name, UnfreezeMethod.Online);
            }}
            className={styles.unfreezeButton}
          >
            Unfreeze online
          </Button>
          <a
            className={styles.providerText}
            href={`tel:${provider.phone}`}
            onClick={() => handleAnalytics(provider.name, UnfreezeMethod.Phone)}
          >
            or call {provider.phone}.
          </a>
        </div>
      ))}
      {rerunFailed && (
        <UserSessionWarning
          message="It looks like your Equifax credit file is still frozen. Please check it and try again"
          showContactUsOption
        />
      )}
      <h3 className={styles.allDoneText}>All done?</h3>
      <Button className={styles.primaryButton} isLoading={isLoading} onClick={handleRerunOffer}>
        Continue application
      </Button>
      <p className={styles.disclaimer}>You can freeze your credit again after you finish your application.</p>
    </FormContainer>
  );
};

export default UnfreezeCreditScore;
